import { DragEvent, useState } from 'react';
import { CustomEquipment } from '../types/sharedTypeImpl.ts';
import { CmmBadge } from './cmmBadge.tsx';
import { ModalDialog } from './modalDialog.tsx';

interface Props {
    machineIds: string[];
    equipments?: CustomEquipment[];
    onSubmit: (machineIds: string[]) => void;
    onClose: () => void;
}

export function AllowedMachinesDialog({
    machineIds,
    equipments,
    onSubmit,
    onClose
}: Props) {
    const [selectedIds, setSelectedIds] = useState(machineIds);
    const [draggedId, setDraggedId] = useState<string | null>(null);
    const [dropId, setDropId] = useState<string | null>(null);
    const available = equipments?.filter(it => it.isActive && !selectedIds.includes(it._id!));
    const selectedRows = selectedIds.map((id, index) => ({
        id,
        equipment: equipments?.find(it => it._id === id),
        index,
    }));
    const availableRows = (available ?? []).map(equipment => ({
        id: equipment._id!,
        equipment,
        index: null,
    }));
    const rows = [...selectedRows, ...availableRows];

    function moveMachine(index: number, offset: number) {
        const reordered = [...selectedIds];
        const [id] = reordered.splice(index, 1);
        reordered.splice(index + offset, 0, id);
        setSelectedIds(reordered);
    }

    function toggleMachine(id: string) {
        const updatedIds = selectedIds.includes(id)
            ? selectedIds.filter(it => it !== id)
            : [...selectedIds, id];
        setSelectedIds(updatedIds);
    }

    function handleDragStart(event: DragEvent, id: string) {
        event.dataTransfer.setData('text/plain', id);
        event.dataTransfer.effectAllowed = 'move';
        setDraggedId(id);
    }

    function handleDragOver(event: DragEvent, id: string, index: number | null) {
        if (!draggedId || index === null || id === draggedId) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setDropId(id);
    }

    function clearDrag() {
        setDraggedId(null);
        setDropId(null);
    }

    function handleDrop(event: DragEvent, index: number | null) {
        event.preventDefault();
        const sourceIndex = selectedIds.indexOf(draggedId ?? '');
        if (sourceIndex !== -1 && index !== null && sourceIndex !== index) {
            moveMachine(sourceIndex, index - sourceIndex);
        }
        clearDrag();
    }

    function handleSubmit() {
        onSubmit(selectedIds);
        onClose();
    }

    return (
        <ModalDialog
            isOpen={true}
            onCancel={onClose}
            frameStyle={{ maxWidth: '90vw' }}
        >
            <h2>Allowed machines</h2>
            <div style={{ overflowX: 'auto' }}>
                <table className='table__small-padding'>
                    <thead>
                        <tr>
                            <th>Included</th>
                            <th>Machine</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(({ id, equipment, index }) => (
                            <tr
                                key={id}
                                draggable={index !== null}
                                onDragStart={event => handleDragStart(event, id)}
                                onDragOver={event => handleDragOver(event, id, index)}
                                onDragLeave={event => {
                                    const nextTarget = event.relatedTarget as Node | null;
                                    if (!event.currentTarget.contains(nextTarget)) setDropId(null);
                                }}
                                onDrop={event => handleDrop(event, index)}
                                onDragEnd={clearDrag}
                                style={{
                                    cursor: index !== null ? 'grab' : undefined,
                                    opacity: draggedId === id ? 0.45 : undefined,
                                    backgroundColor: dropId === id ? '#cae8b0' : undefined,
                                    outline: dropId === id ? '2px solid #04aa6d' : undefined,
                                    outlineOffset: '-2px',
                                }}
                            >

                                <td
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => toggleMachine(id)}
                                >
                                    <input
                                        type='checkbox'
                                        aria-label={`Include ${equipment?.displayName ?? id}`}
                                        checked={index !== null}
                                        onClick={event => event.stopPropagation()}
                                        onChange={() => toggleMachine(id)}
                                    />
                                </td>

                                <td style={{ textAlign: 'left' }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        {equipment && <CmmBadge {...equipment} />}
                                        <span style={{ margin: '6px 8px 6px 0px' }}>
                                            {equipment?.displayName ?? id}
                                        </span>
                                        {index !== null && <span
                                            title='Drag to reorder'
                                            style={{ marginLeft: 'auto' }}
                                        >⠿</span>}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {!equipments && <tr><td colSpan={2}>Loading...</td></tr>}
                    </tbody>
                </table>
            </div>
            <div className='dialog--button-bar'>
                <button type='button' onClick={onClose}>Cancel</button>
                <button type='button' onClick={handleSubmit}>Submit</button>
            </div>
        </ModalDialog>
    );
}
